import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router";
import { Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell, faBellSlash, IconDefinition } from "@fortawesome/free-regular-svg-icons";
import { faBox, faStar } from "@fortawesome/free-solid-svg-icons";

const API_BASE = process.env.REACT_APP_API_URL || "https://api.hataoo.in";

// ─── Types ────────────────────────────────────────────────────────────────────
interface ContactItem {
  _id: string;
  fname?: string;
  lname?: string;
  email?: string;
  contactNo?: string;
  message?: string;
  platform?: string;
  username?: string;
  read?: boolean;
  createdAt?: string;
  _type: "product" | "influencer";
}

interface ReportRequest {
  _id: string;
  name?: string;
  phone?: string;
  message?: string;
  read?: boolean;
  createdAt?: string;
  _docId?: string;
  code?: string;
  ownerName?: string;
  _type: "report";
}

type AnyItem = ContactItem | ReportRequest;

interface Counts {
  product: number;
  influencer: number;
  report: number;
}

interface Items {
  product: ContactItem[];
  influencer: ContactItem[];
  report: ReportRequest[];
}

interface Tab {
  key: string;
  label: string;
  count: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getToken = (): string | null => localStorage.getItem("adminToken");
const authHeaders = (): Record<string, string> => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${getToken()}`,
});

async function apiFetch(url: string, opts: RequestInit = {}): Promise<any> {
  const res = await fetch(`${API_BASE}${url}`, { headers: authHeaders(), ...opts });
  return res.json();
}

// ─── NotifRow ─────────────────────────────────────────────────────────────────
interface NotifRowProps {
  item: AnyItem;
  onToggleRead: (item: AnyItem) => void;
}

function NotifRow({ item, onToggleRead }: NotifRowProps) {
  const [hovered, setHovered] = useState(false);
  const [btnHovered, setBtnHovered] = useState(false);

  const typeConfig: Record<
    string,
    { label: string; bg: string; color: string; icon: IconDefinition; }
  > = {
    product: { label: "Product", bg: "#ede9fe", color: "#5b21b6", icon: faBox },
    influencer: { label: "Influencer", bg: "#fce7f3", color: "#9d174d", icon: faStar },
    report: { label: "Report", bg: "#fef3c7", color: "#92400e", icon: faBell },
  };
  const cfg = typeConfig[item._type];

  const isRead = !!item.read;

  const title =
    item._type === "report"
      ? `${(item as ReportRequest).name || "Anonymous"} — ${(item as ReportRequest).code || ""}`
      : `${(item as ContactItem).fname || ""} ${(item as ContactItem).lname || ""}`.trim() ||
      (item as ContactItem).email ||
      "";

  const sub =
    item._type === "report"
      ? (item as ReportRequest).message || (item as ReportRequest).phone || ""
      : (item as ContactItem).message || (item as ContactItem).email || "";

  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        padding: "11px 16px",
        borderBottom: "1px solid #f3f4f6",
        background: hovered ? "#f9fafb" : isRead ? "#fafafa" : "#fff",
        transition: "background 0.15s",
        cursor: "default",
        opacity: isRead ? 0.65 : 1,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Unread dot */}
      <div
        style={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          background: isRead ? "transparent" : "#7C7FFF",
          flexShrink: 0,
          marginTop: 14,
          transition: "background 0.2s",
        }}
      />

      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 9,
          background: cfg.bg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 16,
          flexShrink: 0,
        }}
      >
        <FontAwesomeIcon icon={cfg.icon} />
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}
        >
          <span
            style={{
              fontWeight: isRead ? 500 : 600,
              fontSize: 13,
              color: isRead ? "#6b7280" : "#111",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: 160,
            }}
          >
            {title}
          </span>
          <span
            style={{
              fontSize: 10,
              fontWeight: 600,
              padding: "1px 6px",
              borderRadius: 6,
              background: cfg.bg,
              color: cfg.color,
              flexShrink: 0,
            }}
          >
            {cfg.label}
          </span>
        </div>
        <p
          style={{
            margin: 0,
            fontSize: 12,
            color: "#6b7280",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: 220,
          }}
        >
          {sub}
        </p>
        {item.createdAt && (
          <p style={{ margin: "3px 0 0", fontSize: 11, color: "#9ca3af" }}>
            {new Date(item.createdAt).toLocaleString("en-IN", {
              day: "numeric",
              month: "short",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </p>
        )}
      </div>

      {/* Toggle read / unread button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onToggleRead(item);
        }}
        title={isRead ? "Mark as unread" : "Mark as read"}
        style={{
          flexShrink: 0,
          width: 28,
          height: 28,
          borderRadius: 7,
          border: `1px solid ${btnHovered ? "#7C7FFF" : "#e5e7eb"}`,
          background: btnHovered
            ? isRead
              ? "rgba(124,127,255,0.08)"
              : "#ede9fe"
            : "transparent",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#7C7FFF",
          fontSize: 13,
          transition: "all 0.15s",
        }}
        onMouseEnter={() => setBtnHovered(true)}
        onMouseLeave={() => setBtnHovered(false)}
      >
        {isRead ? (
          /* unread icon — open circle */
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <circle cx="6" cy="6" r="5" stroke="#7C7FFF" strokeWidth="1.5" />
          </svg>
        ) : (
          /* read icon — checkmark */
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path
              d="M2 6.5L4.5 9L10 3"
              stroke="#7C7FFF"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </button>
    </div>
  );
}

// ─── NotificationBell ─────────────────────────────────────────────────────────
function NotificationBell() {
  const [open, setOpen] = useState<boolean>(false);
  const [counts, setCounts] = useState<Counts>({ product: 0, influencer: 0, report: 0 });
  const [items, setItems] = useState<Items>({ product: [], influencer: [], report: [] });
  const [activeTab, setActiveTab] = useState<string>("all");
  const [loading, setLoading] = useState<boolean>(false);
  const ref = useRef<HTMLDivElement>(null);

  const totalUnread = counts.product + counts.influencer + counts.report;

  // ── fetch just the badge counts (runs every 30 s) ─────────────────────────
  const fetchCounts = useCallback(async (): Promise<void> => {
    try {
      const [contactRes, reportRes] = await Promise.all([
        apiFetch("/api/contact/message/unread-count"),
        apiFetch("/api/contact/report/unread-count"),
      ]);
      setCounts({
        product: contactRes?.data?.productCount ?? 0,
        influencer: contactRes?.data?.influencerCount ?? 0,
        report: reportRes?.data?.count ?? 0,
      });
    } catch (_) { }
  }, []);

  // ── fetch items (runs when panel opens) ──────────────────────────────────
  const fetchItems = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const [prodRes, infRes, repRes] = await Promise.all([
        apiFetch("/api/contact/message/read?type=product&limit=50"),
        apiFetch("/api/contact/message/read?type=influencer&limit=50"),
        apiFetch("/api/contact/report/read?limit=50"),
      ]);

      // Normalise the `read` field: lean() skips schema defaults so old
      // records may have read=undefined. Treat undefined as false (unread).
      const normaliseRead = (val: any): boolean => val === true;

      const tagContact = (arr: any[], type: ContactItem["_type"]): ContactItem[] =>
        (arr || []).map((x: any) => ({
          ...x,
          _type: type,
          read: normaliseRead(x.read),
        }));

      // Flatten report docs → individual request rows, keep read + unread
      // so the toggle button works both ways without a re-fetch.
      const reportRows: ReportRequest[] = (repRes?.data || []).flatMap(
        (doc: any) =>
          (doc.requests || []).map((r: any): ReportRequest => ({
            _id: r._id,
            name: r.name,
            phone: r.phone,
            message: r.message,
            read: normaliseRead(r.read),
            // subdoc timestamps come back as createdAt on the sub-document;
            // fall back to the parent doc's createdAt if missing
            createdAt: r.createdAt || doc.createdAt,
            _type: "report",
            _docId: doc._id,
            code: doc.code,
            ownerName: doc.ownerName,
          }))
      );

      setItems({
        product: tagContact(prodRes?.data || [], "product"),
        influencer: tagContact(infRes?.data || [], "influencer"),
        report: reportRows,
      });
    } catch (_) { }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchCounts();
    const id = setInterval(fetchCounts, 30_000);
    return () => clearInterval(id);
  }, [fetchCounts]);

  useEffect(() => {
    if (open) fetchItems();
  }, [open, fetchItems]);

  // close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ── toggle read / unread for a single item ────────────────────────────────
  const toggleRead = async (item: AnyItem): Promise<void> => {
    const nextRead = !item.read;

    // Optimistic UI update
    if (item._type === "report") {
      setItems((prev) => ({
        ...prev,
        report: prev.report.map((x) =>
          x._id === item._id ? { ...x, read: nextRead } : x
        ),
      }));
      setCounts((prev) => ({
        ...prev,
        report: Math.max(0, prev.report + (nextRead ? -1 : 1)),
      }));
    } else {
      const key = item._type as "product" | "influencer";
      setItems((prev) => ({
        ...prev,
        [key]: (prev[key] as ContactItem[]).map((x) =>
          x._id === item._id ? { ...x, read: nextRead } : x
        ),
      }));
      setCounts((prev) => ({
        ...prev,
        [key]: Math.max(0, prev[key] + (nextRead ? -1 : 1)),
      }));
    }

    // API call
    try {
      if (item._type === "report") {
        // PUT /api/contact/report/request/read/:reqId
        await apiFetch(`/api/contact/report/request/read/${item._id}`, {
          method: "PUT",
          body: JSON.stringify({ read: nextRead }),
        });
      } else {
        // PUT /api/contact/message/update/:id?type=product|influencer
        await apiFetch(`/api/contact/message/update/${item._id}?type=${item._type}`, {
          method: "PUT",
          body: JSON.stringify({ read: nextRead }),
        });
      }
    } catch (_) {
      // Rollback on failure
      fetchItems();
      fetchCounts();
    }
  };

  // ── mark all visible (unread) items as read ──────────────────────────────
  const markAllRead = async (): Promise<void> => {
    const visible = visibleItems(); // already filtered to unread by visibleItems()
    if (!visible.length) return;

    // IDs of items being marked read (for targeted optimistic update)
    const ids = new Set(visible.map((x) => x._id));

    // Optimistic — flip only the targeted items to read
    setItems((prev) => {
      const flipTargeted = <T extends AnyItem>(arr: T[]): T[] =>
        arr.map((x) => (ids.has(x._id) ? { ...x, read: true } : x));
      return {
        product: flipTargeted(prev.product as AnyItem[]) as ContactItem[],
        influencer: flipTargeted(prev.influencer as AnyItem[]) as ContactItem[],
        report: flipTargeted(prev.report as AnyItem[]) as ReportRequest[],
      };
    });
    setCounts((prev) => {
      if (activeTab === "all") return { product: 0, influencer: 0, report: 0 };
      if (activeTab === "report") return { ...prev, report: 0 };
      return { ...prev, [activeTab]: 0 };
    });

    // Fire all API calls in parallel
    await Promise.allSettled(
      visible.map((item) => {
        if (item._type === "report") {
          return apiFetch(`/api/contact/report/request/read/${item._id}`, {
            method: "PUT",
            body: JSON.stringify({ read: true }),
          });
        }
        return apiFetch(
          `/api/contact/message/update/${item._id}?type=${item._type}`,
          { method: "PUT", body: JSON.stringify({ read: true }) }
        );
      })
    );

    // Sync counts from server
    fetchCounts();
  };

  // ── items visible in the current tab (unread only — matches badge count) ──
  const unreadOnly = (arr: AnyItem[]): AnyItem[] => arr.filter((x) => !x.read);

  const visibleItems = (): AnyItem[] => {
    if (activeTab === "product") return unreadOnly(items.product);
    if (activeTab === "influencer") return unreadOnly(items.influencer);
    if (activeTab === "report") return unreadOnly(items.report);
    // "all" tab: merge all unread, newest first
    return unreadOnly([
      ...items.product,
      ...items.influencer,
      ...items.report,
    ]).sort(
      (a, b) =>
        new Date(b.createdAt || 0).getTime() -
        new Date(a.createdAt || 0).getTime()
    );
  };

  const tabs: Tab[] = [
    { key: "all", label: "All", count: totalUnread },
    { key: "product", label: "Product", count: counts.product },
    { key: "influencer", label: "Influencer", count: counts.influencer },
    { key: "report", label: "Reports", count: counts.report },
  ];

  // count unread among currently visible items
  const visibleUnread = visibleItems().filter((x) => !x.read).length;

  return (
    <div ref={ref} style={{ position: "relative" }}>
      {/* ── Bell button ───────────────────────────────────────────────────── */}
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label="Notifications"
        className={`
    relative flex items-center justify-center
    w-10 h-10 rounded-xl
    border border-indigo-300/30
    transition-all duration-200
    text-indigo-500
    ${open ? "bg-indigo-500/10" : "bg-transparent"}
  `}
      >
        <FontAwesomeIcon icon={faBell} className="text-lg" />

        {totalUnread > 0 && (
          <span
            className="
        absolute -top-1 -right-1
        min-w-[18px] h-[18px]
        px-1
        flex items-center justify-center
        rounded-full
        bg-red-500 text-white
        text-[10px] font-semibold
        border border-white
      "
          >
            {totalUnread}
          </span>
        )}
      </button>

      {/* ── Dropdown panel ────────────────────────────────────────────────── */}
      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            right: 0,
            width: 450,
            maxHeight: 530,
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: 14,
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
            display: "flex",
            flexDirection: "column",
            zIndex: 9999,
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <div
            style={{ padding: "14px 16px 0", borderBottom: "1px solid #f1f1f1" }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 12,
              }}
            >
              <span style={{ fontWeight: 600, fontSize: 15, color: "#111" }}>
                Notifications

              </span>

              {visibleUnread > 0 && (
                <button
                  onClick={markAllRead}
                  style={{
                    fontSize: 12,
                    color: "#7C7FFF",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    padding: 0,
                  }}
                >
                  Mark all read
                </button>
              )}
            </div>

            {/* Tabs */}
            <div style={{ display: "flex", gap: 4 }}>
              {tabs.map((t) => (
                <button className="text-sm"
                  key={t.key}
                  onClick={() => setActiveTab(t.key)}
                  style={{
                    fontWeight: 500,
                    padding: "5px 10px",
                    borderRadius: "8px 8px 0 0",
                    border: "none",
                    cursor: "pointer",
                    background: activeTab === t.key ? "#7C7FFF" : "transparent",
                    color: activeTab === t.key ? "#fff" : "#6b7280",
                    transition: "all 0.15s",
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                  }}
                >
                  {t.label}
                  {t.count > 0 && (
                    <span className="w-7 h-7 flex items-center justify-center rounded-md"
                      style={{
                        background:
                          activeTab === t.key
                            ? "rgba(255,255,255,0.3)"
                            : "#f3f4f6",
                        color: activeTab === t.key ? "#fff" : "#374151",
                        fontWeight: 600,
                        minWidth: 16,
                        textAlign: "center",
                      }}
                    >
                      {t.count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Items list */}
          <div style={{ overflowY: "auto", flex: 1 }}>
            {loading ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "32px 0",
                  color: "#9ca3af",
                  fontSize: 13,
                }}
              >
                Loading…
              </div>
            ) : visibleItems().length === 0 ? (
              <div className="text-center py-10 px-4">
  <div className="mb-2">
    <FontAwesomeIcon
      icon={faBellSlash}
      className="text-3xl text-gray-400"
    />
  </div>

  <div className="text-sm text-gray-500">
    No notifications
  </div>
</div>
            ) : (
              visibleItems().map((item) => (
                <NotifRow
                  key={`${item._type}-${item._id}`}
                  item={item}
                  onToggleRead={toggleRead}
                />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── UserDropdown ─────────────────────────────────────────────────────────────
export default function UserDropdown() {
  const navigate = useNavigate();

  const handleLogout = (): void => {
    localStorage.removeItem("adminToken");
    sessionStorage.removeItem("authPin");
    sessionStorage.removeItem("ImageAuthPin");
    sessionStorage.removeItem("VideoAuthPin");
    sessionStorage.removeItem("AudioAuthPin");
    sessionStorage.removeItem("CoverAuthPin");
    navigate("/login");
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <NotificationBell />
      <Button
        className="p-3 rounded-lg text-white py-2 d-flex gap-3 align-items-center bg-[#7C7FFF]"
        onClick={handleLogout}
      >
        <div className="logout-icon ps-1"></div>
        Logout
      </Button>
    </div>
  );
}